import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Briefcase, Loader2, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { JobCard } from "@/components/jobs/JobCard"
import { JobFilters } from "@/components/jobs/JobFilters"
import { getJobs, getJobsWithAuthors } from "@/lib/db"
import { useAuth } from "@/context/AuthContext"
import { MOCK_JOBS } from "@/data/mockJobs"
import type { JobWithAuthor } from "@/types"

export default function JobsPage() {
  const { firebaseUser } = useAuth()
  const [jobs, setJobs] = useState<JobWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const [jobType, setJobType] = useState("all")
  const [isRemote, setIsRemote] = useState(false)

  useEffect(() => {
    async function loadJobs() {
      try {
        setLoading(true)
        const options: Parameters<typeof getJobs>[0] = {
          status: "published",
          orderByField: "created_at",
          orderDirection: "desc",
          limitCount: 50,
        }

        if (jobType !== "all") options.jobType = jobType
        if (isRemote) options.isRemote = true

        const result = await getJobs(options)
        const withAuthors = await getJobsWithAuthors(result.jobs)

        // Fall back to mock data if no real jobs exist yet
        if (withAuthors.length > 0) {
          setJobs(withAuthors)
        } else {
          let filtered = MOCK_JOBS
          if (jobType !== "all") filtered = filtered.filter(j => j.jobType === jobType)
          if (isRemote) filtered = filtered.filter(j => j.isRemote)
          setJobs(filtered)
        }
      } catch {
        // DB table may not exist yet — show mock data
        let filtered = MOCK_JOBS
        if (jobType !== "all") filtered = filtered.filter(j => j.jobType === jobType)
        if (isRemote) filtered = filtered.filter(j => j.isRemote)
        setJobs(filtered)
      } finally {
        setLoading(false)
      }
    }

    loadJobs()
  }, [jobType, isRemote])

  const handleReset = () => {
    setJobType("all")
    setIsRemote(false)
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center text-3xl font-bold text-gray-900 dark:text-white">
            <Briefcase className="mr-3 h-8 w-8 text-indigo-600" />
            Job Board
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Find React Native jobs or post an opening for your team.
          </p>
        </div>
        {firebaseUser && (
          <Link to="/post-job">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Post a Job
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6">
        <JobFilters
          jobType={jobType}
          isRemote={isRemote}
          onJobTypeChange={setJobType}
          onRemoteChange={setIsRemote}
          onReset={handleReset}
        />
      </div>

      {/* Job listings */}
      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
          <Briefcase className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">No jobs found</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            {jobType !== "all" || isRemote
              ? "Try adjusting your filters."
              : "Be the first to post a job listing!"}
          </p>
          {firebaseUser && (
            <Link to="/post-job" className="mt-4">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Post a Job
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  )
}
